/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import moment from 'moment';
import {getInputsOnOffFormat, getItemKey} from '@ion/core/src/entities/Asset';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {isRestricted} from '@ion/core/src/entities/LabelRulesValidator';
import {translate} from '@ion/core/src/locale/locale';
import {assetStateMapping, resolveDriverSearchTerms} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {
  AssetState,
  AssetType,
  BaseAsset,
  Crumb,
  CVSAssetType,
  GenericObject,
  SCDeviceType,
  ToolAssetType
} from '@ion/core/src/types/entities';
import {FilterTypes} from '@ion/core/src/types/services';
import {getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import {
  nextDay
} from '@ion/core/src/utils/DateUtil';
import {
  safeJSONParse
} from '@ion/core/src/utils/JSONUtils';
import {
  Mapped
} from '@ion/core/src/utils/ObjectUtil';
import {
  splitBySpecialCharacters,
  stringWithEscapeCharacters,
  titleCase
} from '@ion/core/src/utils/StringUtil';
import {
  FilterModelType,
  normalizeAccountField,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  BreadcrumbState,
  FilterItems,
  TrackingFilters,
  TrackingMode
} from '@ion/dls/src/types/types';

export const distanceColumnName = 'distance';

export const breadcrumbWidth = {
  table     : 450,
  sideSheet : 286
};

// Defines the interval to grid's scrolling bar
export const scrollToInterval = {
  clientMode : 1500,
  serverMode : 3000
};

// Defines the scroll item's ids
export const scrollToItemId = {
  assetId  : 'assetId',
  deviceId : 'deviceIdValue'
};

export const assetFilter = {
  deviceAttribute : null,
  cvsAttribute    : 'assetId'
};

export const deviceFilter = {
  deviceAttribute : 'id',
  cvsAttribute    : null
};

const filterTypeMapping = {
  inRange     : 'in range',
  lessThan    : 'less than',
  greaterThan : 'greater than',
  notEqual    : 'not equals',
  startsWith  : 'starts with',
  endsWith    : 'ends with',
  equals      : 'equals',
  contains    : 'contains'
};

// Defines the scroll props
interface ScrollToItemProp {
  dataList: Device[],
  listComponent: React.MutableRefObject<any>;
  selectedItem: Device | CVSAssetType,
  callback?: Function;
  align?: string;
  delay?: number;
};

type FilterProps = {
  deviceAttribute: string,
  cvsAttribute: string
};

type ConvertTextFilterProps = {
  columnName: string,
  filterAttribute: string,
  assetStateList: any,
  useServiceV2: boolean,
  data: any
};

const ADDRESS_FIELD = 'LOWER(CONCAT(COALESCE(CONCAT(street,\', \'),\'\'),CONCAT(COALESCE(CONCAT(city,\', \'), \'\'),CONCAT(COALESCE(CONCAT(state,\' \'), \'\'), COALESCE(postalCode, \'\')))))';

export const commentsLabels = () => translate('tracking:buttons', {returnObjects: true});

/**
 * The formatSearchTerm function provides a simply utility which format the input string
 * by removing spaces,trailing comma & extra spaces from the input string.
 *
 * ```typescript
 *
 *   import {formatSearchTerm} from '@/routes/tracking/trackingUtils';
 *   const formattedString = formatSearchTerm('Young, Mark'); //output: Young Mark
 *
 * ```
 */
export const formatSearchTerm = (searchTerm: string): string => {
  const terms =  searchTerm.split(' ').filter(i => i);
  return terms.map(item => item.replace(/(^,)|(,$)/g, '').trim()).join(' ');
};

/**
 * Provides a utility which returns all non-SC Device Assets.
 */
export const filterCVSAssets = (assets: Device[]): Device[] => (
  assets?.filter(asset => !asset?.isScDevice?.())
);

/**
 * Provides a utility which verify the breadcrumbs state.
 */
export const isBreadCrumbState = (crumbs: Crumb[], state: BreadcrumbState): boolean => {
  return crumbs?.length === state;
};

const normalizeQuery = (query) => {
  return query.length ? ' AND ' : '';
};

/**
 * Defines a utility method to remove 'UI' from the Client-Side attributes
 */
const normalizeColName = (colName: string) => {
  return colName?.endsWith('UI') ? colName?.substring(0, colName?.length - 2) : colName;
};

/**
 * Provides a utility to get the ignition & inputs state label.
 */
export const getInputsOnOffLabel = (value, capitalize = true) => {
  const onLabel = translate('common:switchStatus.on');
  const offLabel = translate('common:switchStatus.off');
  const result = getInputsOnOffFormat(value, onLabel, offLabel);
  if (capitalize) {
    return result?.toUpperCase();
  }
  return result;
};

/*
* Provides a custom filter utility to create ArcGIS Feature services
* definition from grid filter model.
*/
export const filterModelConverter = (
  filterModel: FilterModelType = {},
  replacements: GenericObject = {},
  useServiceV2: boolean = false
): string | null => {
  let string = '';
  const assetStateList = translate('common:assetState', {returnObjects: true});
  Object.entries(filterModel).forEach((filterData, i) => {
    const [columnName, data = {filterType: '', filter: ''}] = filterData;
    const {filterType} = data;

    if (!filterType) return null;
    const filterAttribute = replacements[columnName] || columnName;
    if (filterType === 'date' && string === '') {
      string += convertDateFilter(data, normalizeColName(filterAttribute));
    } else if (filterType === 'date' && string !== '') {
      string += `${normalizeQuery(string)}${(convertDateFilter(data, normalizeColName(filterAttribute)))}`;
    };

    if (i > 0 && filterType !== 'date') {
      string += `${normalizeQuery(string)}${(convertTextFilter({columnName, filterAttribute, data, useServiceV2, assetStateList}))}`;
    } else if (filterType !== 'date') {
      string +=  convertTextFilter({columnName, filterAttribute, data, useServiceV2, assetStateList});
    };
  });
  return string;
};

// returns the CTC filter value based on columnName and its UI value
const getFilterValue = (columnName, value) => {
  if (columnName === 'alertStatus') {
    return value.toLowerCase() === 'no' ? 'Active' : 'Acknowledged';
  }
  return value;
};

/**
 * Provides a custom filter utility to create ArcGIS Feature services V2 APIs
 * definition from grid filter model. Can be used in future services where filters
 * needs to be sent in payload as array of object instaed of query injection
 */
export const filterModelConverterV2 = (
  filterModel: FilterModelType = {},
  replacements: GenericObject = {}
): Array<any> | null => {
  const filtersArray = [];
  let filterobj = {};
  Object.entries(filterModel).forEach((filterData, i) => {
    const [columnName, data = {filterType: '', filter: '', type: '', dateFrom: '', dateTo: ''}] = filterData;
    const {filterType, type, filter, dateFrom, dateTo} = data;

    if (!filterType) return null;
    const filterAttribute = replacements[columnName] || columnName;
    if (filterAttribute === 'eventTime' || filterType === 'date') {
      filterobj = {
        field : normalizeColName(filterAttribute),
        ops   : type === 'equals' ? filterTypeMapping.inRange : filterTypeMapping[type],
        type  : 'datetime',
        value : type === 'greaterThan' ? moment(dateFrom).endOf('day').toISOString() : moment(dateFrom).startOf('day').toISOString(),
        ...((type === 'inRange' || type === 'equals') && {value1: moment(dateTo || dateFrom).endOf('day').toISOString()})
      };
      filtersArray.push(filterobj);
    } else if (filterType === 'set') {
      if (data.values?.length) {
        filterobj = {
          field : normalizeColName(filterAttribute),
          value : data.values
        };
        filtersArray.push(filterobj);
      }
    } else {
      filterobj = {
        field : normalizeColName(filterAttribute),
        ops   : filterTypeMapping[type],
        type  : filterType,
        value : getFilterValue(filterAttribute, filter)
      };
      filtersArray.push(filterobj);
    }
  });
  return filtersArray;
};

/**
 * Provides a utility to return text filter query
 */
export const convertTextFilter = ({
  columnName,
  filterAttribute,
  data,
  assetStateList,
  useServiceV2 = false
}: ConvertTextFilterProps) => {
  const {filter, type} = data;
  const modifiedFilter = filter?.toLocaleLowerCase();
  switch (columnName) {
    case 'addressUI':
      return getAddressMapping(filterAttribute, type, modifiedFilter);
    case 'stateUI': {
      const filterValue = getItemKey(assetStateList, modifiedFilter) || modifiedFilter;
      return getUtilizationMapping(type, filterValue?.toLocaleLowerCase());
    }
    case 'acknowledge':
      return getAcknowledgeEvents(filterAttribute, data);
    case 'operatorNamesUI':
      return resolveDriverSearchTerms(filter, 'AND', false);
    default:
      return convertCVSTextFilter(normalizeColName(filterAttribute), data, useServiceV2);
  }
};

/**
 * Provides a utility to return address query
 */
const getAddressMapping = (colName: string, type: string, filter: string): string => {
  switch (type) {
    case 'equals':
      return `(${ADDRESS_FIELD} = ${getTextColumnMapping(type, filter)})`;
    case 'contains':
      return `(${ADDRESS_FIELD} like ${getTextColumnMapping(type, filter)})`;
    case 'endsWith':
    case 'startsWith':
    {
      const query = getTextColumnMapping(type, filter);
      const querylike = `${ADDRESS_FIELD} like ${query}`;
      return `(${querylike})`;
    }
    default: {
      const term = getTextColumnMapping(type, filter);
      const like = `${ADDRESS_FIELD} like ${term}`;
      return `${colName} like ${term} OR ${like}`;
    }
  }
};

/**
 * Provides a utility to return CVS query
 */
export const convertCVSTextFilter = (filterAttribute: string, data: any, useServiceV2: boolean = false) => {
  const {filter, type, filterType, values} = data;
  const isRestrictedRole = isRestricted(EntityLabel.getRole());
  const filterProperty = !useServiceV2 || isRestrictedRole ? filterAttribute : `LOWER(${filterAttribute})`;
  if (filterType === 'set') {
    const filterValues = '(\''+values.join('\',\'').toLowerCase()+'\')';
    return `${filterProperty} in ${filterValues}`;
  }
  switch (type) {
    case FilterTypes.Equals:
      return `${filterProperty} = ${getTextColumnMapping(type, filter)}`;
    case FilterTypes.NotEqual:
      return `${filterProperty} != ${getTextColumnMapping(type, filter)}`;
    default:
      return `${filterProperty} like ${getTextColumnMapping(type, filter)}`;
  }
};

/**
 * getUtilizationMapping method helps to return Utilization query
 */
const getUtilizationMapping = (type: string, filter: string) => {
  let query = '';
  Object.keys(assetStateMapping).forEach((item, i) => {
    switch (type) {
      case FilterTypes.Contains:
        if (item.includes(filter)) {
          query = assetStateMapping[item];
        }
        break;
      case FilterTypes.StartsWith:
        if (item.startsWith(filter)) {
          query = assetStateMapping[item];
        }
        break;
      case FilterTypes.EndsWith:
        if (item.endsWith(filter)) {
          query = assetStateMapping[item];
        }
        break;
      case FilterTypes.Equals:
        if (item === filter) {
          query = assetStateMapping[item];
        }
        break;
      default: {
        query = '1=0';
      }
    }
  });
  return query.length ? query : '1=0';
};

/*
* getTextColumnMapping method helps to get text column query
*/
const getTextColumnMapping = (type: string, filter: string): string => {
  const modifiedFilter = filter?.toLocaleLowerCase().replaceAll('\'', '\'\'');
  switch (type) {
    case FilterTypes.Equals:
      return `'${modifiedFilter}'`;
    case FilterTypes.Contains:
      return `'%${modifiedFilter}%'`;
    case FilterTypes.StartsWith:
      return `'${modifiedFilter}%'`;
    case FilterTypes.EndsWith:
      return `'%${modifiedFilter}'`;
    case FilterTypes.NotEqual:
      return `'${modifiedFilter}'`;
    default:
      return '';
  }
};

/*
* getAcknowledgeEvents method by default returns data for unacknowledged events
*/
const getAcknowledgeEvents = (filterAttribute: string, {filter}: any) => {
  const status = {
    [translate('geofence-events:yes')] : 'alertStatus = \'Acknowledged\'',
    [translate('geofence-events:no')]  : 'alertStatus = \'Active\''
  };
  return status[titleCase(filter.toLowerCase())] || `${filterAttribute} = ''`;
};

/**
 *
 * @convertDateFilter
 *
 * Converts a UI specific Date filter type to ArcGIS Date based search
 * Query and adds the filter to the provided filters. Note, the date
 * values provided are converted to UTC and formatted as an ISO8601
 * String as per Connect requirements.
 *
 *``` typescript
 *   let filters = convertDateFilter(
 *     {
 *       dateFrom:  "2017-12-06",
 *       dateTo: null,
 *       filterType: "date",
 *       type: "equals"
 *     },
 *     'eventTime'
 *   );
 *   console.log(JSON.stringify(filters));
 *
 * ```
 *
 * @param  {object} filterModel The UI filter model from which the search
 *         query is to be constructed.
 * @param  {string} attr An optional ArcGIS service attribute to be used in
 *         place of the provided UI property name (specified via the prop
 *         argument).
 */
export const convertDateFilter = (filterData: any, attr: string): string => {
  const {dateFrom, dateTo, type} = filterData;
  const from  = moment(dateFrom).startOf('day').toISOString();
  const to    = moment(dateTo || dateFrom).endOf('day').toISOString();
  return dateFilters[type](from, to, attr);
};

/**
 * Provides conversions of UI specific Date based filter types
 * to their equivalent ArcGIS Search syntax queries.
 */
export const dateFilters = {
  equals      : (dateFrom, dateTo, field) => `(${field} >= '${dateFrom}' AND ${field} <= '${dateTo}')`,
  greaterThan : (dateFrom, dateTo, field) => `${field} > '${nextDay(dateFrom).toISOString()}'`,
  lessThan    : (dateFrom, dateTo, field) => `${field} < '${dateFrom}'`,
  notEqual    : (dateFrom, dateTo, field) => `(${field} NOT BETWEEN '${dateFrom}' AND '${dateTo}')`,
  inRange     : (dateFrom, dateTo, field) => `(${field} >= '${dateFrom}' AND ${field} <= '${dateTo}')`
};

/**
 *
 * @convertSortModel
 *
 * Converts an AG Grid Sort Model to a valid CTC Sort Parameters
 * list.
 *
 */
export const sortModelConverter = (
  sortModel: SortModelType[],
  replacements?: GenericObject
): string[] => {
  const results = [];
  if (sortModel?.length && !isEmpty(sortModel[0])) {
    sortModel.forEach(({colId, sort}) => {
      let attr = replacements ? replacements[colId] || colId : colId;
      attr = normalizeAccountField[attr] || attr;
      const result = (sortModelMap[attr] || sortModelMap.default)(sort, attr);
      results.push(result);
    });
  }
  return results;
};

/**
 * Utility to format address field for sorting
 */
export const sortModelMap = {
  street  : (sort: string, attr?: string) => `street::bytea ${sort}, city ${sort}, state ${sort}, postalcode ${sort}`,
  default : (sort: string, attr: string) => `${attr} ${sort}`
};

/**
 * @getAssociations
 *
 * utility that gets associated CVSAsset to assigned assets and
 * returns a comma concatenated string of all associations
 */
export function getAssociations(asset: CVSAssetType) {
  const {assets} = asset ?? {};
  const parsedAssets = safeJSONParse(assets as unknown as string);
  return parsedAssets ? (parsedAssets.map?.(asset => asset.name).join(', ') ?? '') : '';
}

export const columnResolver = (definitionsMap: Mapped, colId: string) => {
  if (colId === 'assetNameUI' || colId === 'assetName') {
    return definitionsMap?.assetName || definitionsMap?.assetNameUI;
  } else {
    return definitionsMap[colId];
  }
};

/**
 * Utility to return Asset or Device information
 */
export const getItemValue = (row: BaseAsset | Device): BaseAsset => {
  return row instanceof Device ? row?.getDevice() : row;
};

/*
* Defines a utility to return asset data based on assetId
*/
export const getAssetById = (assets: Device[], assetId: number) => {
  return assets?.filter((assetAsDevice: Device) => {
    const asset = getItemValue(assetAsDevice);
    return asset?.assetId === assetId;
  });
};

/*
* Defines a utility to return the correct asset icon based on asset type
*/
export const getAssetIconType = (asset: Device, tool: ToolAssetType): string => {
  if (!asset?.getAssetValue('tagReports')) {
    return AssetType[tool.assetType || 'Tool'];
  }
  return AssetType.ScDevice;
};

/**
 * Defines a utility to return Engine Type based Engine Types from locales files and given value
 */
export const getEngineType = (value: string): string => {
  const options = translate('common:editors.engineType.options', {returnObjects: true});
  if (options?.length && value?.length) {
    const option = options.find(i => i?.value === value.substring(0, 1));
    return option?.label || value;
  }
  return value;
};

// Defines a utility to scroll the list to selected item
export const scrollToItem = ({
  dataList,
  selectedItem,
  listComponent,
  callback,
  align = 'start',
  delay = 900
}: ScrollToItemProp) => {
  const field = selectedItem?.isScDevice ? scrollToItemId.deviceId : scrollToItemId.assetId;
  const value = selectedItem[field];
  const index = dataList?.findIndex((item: Device) => (item?.getAssetValue(field) === value));
  const scroll = setTimeout(() => {
    listComponent?.scrollToItem(index, align);
    callback?.(true);
    clearTimeout(scroll);
  }, delay);
};

/**
 * Retrieves the events within the event’s inputChanged object and returns the events excluding any ignition events.
 * If the event’s inputChanged object is empty, it returns the default event instead.
 *
 * ```typescript
 *
 * import {getInputChangedEvents} from ‘./trackingUtils’;
 *
 * latestEventUI: getInputChangedEvents(event, defaultEvent);
 *
 * ```
 *
 */
export const getInputChangedEvents = (event: GenericObject, defaultEvent: string) => {
  const {inputChanged} = event || {};
  if (!isNil(inputChanged) && inputChanged !== 'null') {
    const inputEvents     = [];
    const parsedEvent     = safeJSONParse(inputChanged);
    const parsedEventKeys = Object.keys(parsedEvent);
    // If the inputChanged object only contains an ignition event, we want to
    // return the default event so that the event cell is not blank.
    if (parsedEventKeys?.length === 1 && parsedEventKeys?.[0] === 'ignition') {
      return defaultEvent;
    }
    for (const key in parsedEvent) {
      if (key !== 'ignition') {
        inputEvents.push(`${key} ${parsedEvent[key]}`);
      }
    }
    return inputEvents.join(', ');
  }
  return defaultEvent;
};

export const isLiveTracking = (mode = TrackingMode.Live) => mode === TrackingMode.Live;

/*
 * Defines a utility to retrieve the filtered item based on filtered ids.
 *
 * ```typescript
 *
 * import {getFilteredItems} from ‘./trackingUtils’;
 *
 * const data = getFilteredItems(items, filteredIds);
 *
 * ```
 */
export const getFilteredItems = (
  items: Device[] | SCDeviceType[],
  filteredIds: number[],
  filters: FilterProps = assetFilter
) => {
  if (items && filteredIds) {
    return items.filter(item =>
      filteredIds.includes(item?.getItemValue?.(filters)));
  }
  return null;
};

// Defines a utility method to Add or Remove the Distance Column.
export const verifyDistanceColumn = (columns: any[], findNearbyIsActive: boolean) => {
  if (columns) {
    const distanceColumnExists = hasDistanceColumn(columns);
    const distanceUOM      = translate(`common:units.${isImperial(getUserUnit()) ? 'mi' : 'km'}`);
    if (findNearbyIsActive && !distanceColumnExists) {
      columns.unshift({
        headerName      : translate('tracking:tableView.grid.columns.distance', {uom: `(${distanceUOM})`}),
        field           : distanceColumnName,
        pinned          : 'left',
        width           : 160,
        sortable        : true,
        resizable       : true,
        unSortIcon      : true,
        lockPosition    : true,
        suppressMovable : true,
        filter          : false,
        hide            : false
      });
    } else if (!findNearbyIsActive && distanceColumnExists) {
      columns = removeDistanceColumn(columns);
    }
  }
  return columns;
};

// Defines a utility to validate if distance column exists.
const hasDistanceColumn = (columns: any[]) => {
  return columns?.findIndex(({field, colId}) => ((field && field === distanceColumnName) ||
   (colId && colId === distanceColumnName))) >= 0;
};

// Defines a utility to remove distance Column.
const removeDistanceColumn = (columns: any[]) => {
  return columns?.filter(({field, colId}) => (field && field !== distanceColumnName) ||
    (colId && colId !== distanceColumnName));
};
// Defines a utility to remove distance column is Find Nearby is not active
export const columnNormalizer = (columns: any[], resolverOptions: GenericObject) => {
  const {findNearbyIsActive} = resolverOptions || {};
  if (!findNearbyIsActive) {
    return removeDistanceColumn(columns);
  }
  return columns;
};

/**
 * Defines a utility to extract addition params from traking grid filters, which we send to CVS v2 service
 *
 * ```typescript
 *
 * import {getAdditionalFilterParams} from ‘./trackingUtils’;
 *
 * const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
 *
 * ```
 *
 */
export const getAdditionalFilterParams = (filterOn: any[]) => {
  if (filterOn?.length) {
    const assetStateList = translate('common:assetState', {returnObjects: true});
    const {
      value: addressFilter = null,
      ops = null
    } = filterOn.find(({field}) => field === 'street' || field === 'address') ?? {};
    let addressFilterObj = {};
    const statusFilterValue = filterOn.find(({field}) => field === 'state' || field === 'assetStatus')?.value;
    const statusFilter      = Array.isArray(statusFilterValue)
      ? statusFilterValue
      : getItemKey(assetStateList, statusFilterValue) || statusFilterValue;
    if (statusFilter?.length) {
      const stateIndex = filterOn.findIndex(({field}) => field === 'state' || field === 'assetStatus');
      if (stateIndex !== -1) {
        filterOn.splice(stateIndex, 1);
      };
    }
    if (addressFilter?.length) {
      addressFilterObj = {
        searchTerm : addressFilter,
        searchtype : 'address',
        ops
      };
      filterOn.splice(filterOn.findIndex(({field}) => field === 'street' || field === 'address'), 1);
    }
    return {
      statusFilter,
      addressFilterObj
    };
  }
  return {};
};

// Utility to get dbWriteTimeFilter for CVS V2 Service
export const getDbWriteTimeFilter = (time, ops = 'greater than') => ([{
  field : 'dbWriteTime',
  type  : 'datetime',
  value : new Date(time).toISOString(),
  ops
}]);

/**
 * Defines a utility to return assetId based on the assetId or Id from CVS Entity
 *
 * ```typescript
 *
 * import {getAssetId} from ‘./trackingUtils’;
 *
 * const id = getAssetId(asset);
 *
 * ```
 */
export const getAssetId = (asset: Device) => {
  const item = getItemValue(asset);
  return item?.assetId || item?.id;
};

/**
 * Retrieves selected items from a filters object.
 *
 * This utility function processes a `filters` object and extracts the selected values.
 * You can optionally convert the result to a comma-separated string.
 *
 * ### Parameters:
 * - `filters - An object where each key corresponds to a filter item.
 *              Each filter item can either be a simple value or an object containing a `selected` property.
 * - `toString - Optional. If set to `true`, the function returns the selected items
 *               as a comma-separated string. Defaults to `false`.
 *
 * ### Returns:
 * - `any[] | string` - An array of selected items, or a string if `toString` is `true`.
 *
 * ### Example:
 *
 * ```typescript
 * import {getSelected} from './trackingUtils’';
 *
 * const filters = {
 *   category: {selected: 'Electric'}
 * };
 *
 * // Get selected items as an array
 * const selectedArray = getSelected(filters);
 * console.log(selectedArray); // Output: ['Electric']
 *
 * // Get selected items as a string
 * const selectedString = getSelected(filters, true);
 * console.log(selectedString); // Output: "Electric"
 * ```
 */
export const getSelected = (filters: TrackingFilters, toString = false) => {
  const selectedMap = [];
  Object.entries(filters).forEach(([_, state]) => {
    selectedMap.push((state as FilterItems)?.selected ?? state);
  });
  return toString ? selectedMap.toString() : selectedMap;
};

export const toEnumKey = (value: string): string =>
  Object.keys(AssetState).find(
    k => AssetState[k as keyof typeof AssetState] === value
  ) ?? value.toUpperCase();

export const toLocaleKey = (value: string): string =>
  AssetState[value as keyof typeof AssetState] ?? value;
